import React, { useRef } from "react";
import classNames from "classnames";
import { useBreakableText } from "@hooks/useBreakableText";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useTruncationTooltip } from "@design-system/Tooltip/useTruncationTooltip";
import { Side } from "@design-system/overlay/overlayPositioning";
import { useIdCopy } from "@hooks/useIdCopy";
import styles from "./BreakableText.module.scss";

interface BreakableTextProps {
    text: string | null | undefined;
    className?: string;
    showTooltip?: boolean;
    tooltipSide?: Side;
    copyId?: string | number;
    copyLabel?: string;
}

const isClamped = (element: HTMLElement) =>
    element.scrollHeight > element.offsetHeight;

export const BreakableText: React.FC<BreakableTextProps> = ({
    text,
    className,
    showTooltip,
    tooltipSide = "bottom",
    copyId,
    copyLabel,
}) => {
    const { getCopyProps } = useIdCopy({ label: copyLabel });
    const breakableText = useBreakableText(text ?? "");
    const textRef = useRef<HTMLSpanElement>(null);
    const truncationTooltipProps = useTruncationTooltip({
        ref: textRef,
        isTruncated: isClamped,
    });

    const textElement = (
        <span
            className={classNames(className, styles.breakableText)}
            {...getCopyProps(copyId)}
            ref={textRef}
        >
            {breakableText}
        </span>
    );

    if (!showTooltip) {
        return textElement;
    }
    return (
        <Tooltip content={text} side={tooltipSide} {...truncationTooltipProps}>
            {textElement}
        </Tooltip>
    );
};
